import { useCallback, useEffect } from 'react';
import Select from '__components/Inputs/Select';
import useCustomObjectDetails from '__components/Wizards/shared/hooks/useCustomObjectDetails';
import { useGroupedFields } from '__components/Wizards/shared/hooks/useGroupedFields';
import MultiSelect from '__components/Inputs/MultiSelect';
import { useTranslation } from 'react-i18next';
import { useMenuScroller } from '../useMenuScroller';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField, SelectOption } from '@kizenapps/engine';

export const FieldBlock = ({
  field,
  disabled = false,
}: {
  field: AssistantField;
  disabled?: boolean;
}) => {
  const {
    interpolateValue,
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const { t } = useTranslation();

  const objectId = interpolateValue(field.object_id ?? '');

  const { data, isLoading } = useCustomObjectDetails({
    objectId,
    enabled: Boolean(objectId),
  });

  const groupedOptions = useGroupedFields(
    data?.fields,
    data?.fieldCategories,
    false
  );

  const value =
    (state as any)[field.key]?.value ??
    field.default ??
    (field.allow_multiple ? [] : { label: '', value: '' });

  const onChange = useCallback(
    (newValue: SelectOption | Array<SelectOption>) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: {
          type: 'field',
          value: newValue,
          associatedObject: {
            id: objectId,
            name: data?.objectName,
          },
        },
      }));

      afterFieldChange(field.key);
    },
    [field.key, setState, objectId, data?.objectName, afterFieldChange]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setState((prevState: any) => {
        const newState = { ...prevState };
        delete newState[field.key];
        return newState;
      });
    });
  }, [field.key, registerFieldResetter, setState]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  const menuProps = useMenuScroller();

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  if (field.allow_multiple) {
    return (
      <MultiSelect
        options={groupedOptions}
        value={value}
        disabled={!objectId || isDisabled}
        onChange={onChange}
        label={field.label}
        labelInfo={field.tooltip}
        inModal
        placeholder={field.placeholder ?? t('Choose Fields')}
        validate={errorState}
        required={field.required}
        {...menuProps}
      />
    );
  }

  return (
    <>
      <Select
        options={groupedOptions}
        label={field.label}
        labelInfo={field.tooltip}
        disabled={!objectId || isDisabled}
        value={value}
        onChange={onChange}
        isLoading={isLoading}
        validate={errorState}
        required={field.required}
        {...menuProps}
      />
    </>
  );
};
